import { useState, useEffect, useCallback, useRef } from 'react';
import { getPresignedDownloadUrl, invalidatePresignedUrl, type PresignedDownloadData } from './attachmentCache';

export interface UsePresignedFileUrlOptions {
  enabled?: boolean;
  onSuccess?: (data: PresignedDownloadData) => void;
  onError?: (err: unknown) => void;
}

export interface UsePresignedFileUrlResult {
  url: string;
  downloadUrl: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  expiresAt: string | null;
  isLoading: boolean;
  hasError: boolean;
  refresh: (forceRefresh?: boolean) => Promise<string | null>;
}

// Safety margin before expiration to trigger proactive refresh: 2 minutes (120,000 ms)
const EXPIRATION_REFRESH_MARGIN_MS = 2 * 60 * 1000;
const MAX_AUTO_RETRIES = 2;

export function usePresignedFileUrl(
  workspaceId: string | undefined,
  fileId: string | undefined,
  options?: UsePresignedFileUrlOptions
): UsePresignedFileUrlResult {
  const enabled = options?.enabled ?? true;

  // Preserve callbacks in refs to prevent fetchUrl/useEffect re-creation when options object reference changes
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const [data, setData] = useState<PresignedDownloadData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const retryCountRef = useRef<number>(0);
  const requestIdRef = useRef<number>(0);

  const clearRefreshTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchUrl = useCallback(
    async (forceRefresh = false): Promise<string | null> => {
      if (!workspaceId || !fileId || !enabled) {
        return null;
      }

      const currentRequestId = ++requestIdRef.current;

      if (forceRefresh) {
        invalidatePresignedUrl(workspaceId, fileId);
      }

      try {
        setIsLoading(true);
        setHasError(false);

        const result = await getPresignedDownloadUrl(workspaceId, fileId);
        
        // Race condition guard: discard stale out-of-order responses or unmounted component calls
        if (requestIdRef.current !== currentRequestId || !isMountedRef.current) {
          return null;
        }

        setData(result);
        setIsLoading(false);
        retryCountRef.current = 0;
        optionsRef.current?.onSuccess?.(result);

        // Schedule proactive refresh timer before expiresAt
        clearRefreshTimer();
        const expiresTime = new Date(result.expiresAt).getTime();
        const now = Date.now();
        const timeUntilRefresh = expiresTime - now - EXPIRATION_REFRESH_MARGIN_MS;

        if (!isNaN(timeUntilRefresh) && timeUntilRefresh > 0) {
          timerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              fetchUrl(true);
            }
          }, timeUntilRefresh);
        } else if (!isNaN(expiresTime) && expiresTime <= now) {
          // If already expired, refresh immediately
          fetchUrl(true);
        }

        return result.url;
      } catch (err) {
        if (requestIdRef.current !== currentRequestId || !isMountedRef.current) {
          return null;
        }

        console.warn(`[usePresignedFileUrl] Failed to resolve URL for file ${fileId}:`, err);

        // Bounded auto-retries to prevent infinite refresh loops
        if (retryCountRef.current < MAX_AUTO_RETRIES && forceRefresh) {
          retryCountRef.current += 1;
          console.info(`[usePresignedFileUrl] Bounded retry attempt ${retryCountRef.current} for file ${fileId}`);
        } else {
          setHasError(true);
        }

        setIsLoading(false);
        optionsRef.current?.onError?.(err);
        return null;
      }
    },
    [workspaceId, fileId, enabled, clearRefreshTimer]
  );

  const refresh = useCallback(
    async (forceRefresh = true): Promise<string | null> => {
      return fetchUrl(forceRefresh);
    },
    [fetchUrl]
  );

  useEffect(() => {
    isMountedRef.current = true;
    if (workspaceId && fileId && enabled) {
      fetchUrl(false);
    } else {
      setData(null);
      setIsLoading(false);
      setHasError(false);
    }

    return () => {
      isMountedRef.current = false;
      clearRefreshTimer();
    };
  }, [workspaceId, fileId, enabled, fetchUrl, clearRefreshTimer]);

  // Bug 5 fix: visibilitychange attaches to document, focus attaches to window
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      const isVisible = typeof document !== 'undefined' && !document.hidden;
      if (isVisible && workspaceId && fileId && enabled && data?.expiresAt) {
        const expiresTime = new Date(data.expiresAt).getTime();
        if (!isNaN(expiresTime) && expiresTime - Date.now() <= EXPIRATION_REFRESH_MARGIN_MS) {
          fetchUrl(true);
        }
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleVisibilityOrFocus);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleVisibilityOrFocus);
      }
    };
  }, [workspaceId, fileId, enabled, data?.expiresAt, fetchUrl]);

  return {
    url: data?.url || '',
    downloadUrl: data?.downloadUrl || data?.url || '',
    fileName: data?.fileName || '',
    contentType: data?.contentType || '',
    fileSize: data?.fileSize || 0,
    expiresAt: data?.expiresAt || null,
    isLoading,
    hasError,
    refresh,
  };
}
